import React, { useState, useEffect, useRef } from 'react';
import {
  GraduationCap, Search, Filter, CheckCircle, XCircle, Edit, Trash2,
  Eye, EyeOff, Upload, Loader, ChevronLeft, ChevronRight, Building2,
  Mail, Phone, MapPin, Globe, Calendar, Users, Image as ImageIcon, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CollegesAdmin = ({ token }) => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const fetchColleges = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        status: statusFilter
      });
      if (searchQuery) params.append('search', searchQuery);

      const res = await axios.get(`${API}/api/admin/colleges?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setColleges(res.data.colleges || []);
      setTotalPages(res.data.pages || 1);
      setStats(res.data.stats || { pending: 0, approved: 0, rejected: 0, total: 0 });
    } catch (err) {
      toast.error('Failed to fetch colleges');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchColleges();
  }, [statusFilter, currentPage, token]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchColleges();
  };

  const handleApprove = async (collegeId) => {
    try {
      await axios.post(`${API}/api/admin/colleges/${collegeId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('College approved successfully');
      fetchColleges();
    } catch (err) {
      toast.error('Failed to approve college');
    }
  };

  const handleReject = async (collegeId) => {
    try {
      await axios.post(`${API}/api/admin/colleges/${collegeId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('College rejected');
      fetchColleges();
    } catch (err) {
      toast.error('Failed to reject college');
    }
  };

  const handleToggleLanding = async (collegeId) => {
    try {
      await axios.post(`${API}/api/admin/colleges/${collegeId}/toggle-landing`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Landing page visibility updated');
      fetchColleges();
    } catch (err) {
      toast.error('Failed to update visibility');
    }
  };

  const handleDelete = async (collegeId) => {
    if (!window.confirm('Are you sure you want to remove this college? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/admin/colleges/${collegeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('College removed');
      fetchColleges();
    } catch (err) {
      toast.error('Failed to remove college');
    }
  };

  const openEditModal = (college) => {
    setSelectedCollege(college);
    setEditForm({
      college_name: college.college_name || '',
      contact_person: college.contact_person || '',
      phone: college.phone || '',
      location: college.location || '',
      city: college.city || '',
      state: college.state || '',
      website: college.website || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/colleges/${selectedCollege.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('College updated successfully');
      setShowEditModal(false);
      fetchColleges();
    } catch (err) {
      toast.error('Failed to update college');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (collegeId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/api/admin/colleges/${collegeId}/upload-logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Logo uploaded successfully');
      fetchColleges();
    } catch (err) {
      toast.error('Failed to upload logo');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return styles[status] || styles.pending;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <GraduationCap className="text-emerald-400" size={28} />
            College Management
          </h2>
          <p className="text-gray-400 text-sm mt-1">Manage college partnerships and approvals</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="text-3xl font-bold text-white">{stats.total}</div>
          <div className="text-gray-400 text-sm">Total Colleges</div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="text-3xl font-bold text-amber-400">{stats.pending}</div>
          <div className="text-amber-400/70 text-sm">Pending Approval</div>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
          <div className="text-3xl font-bold text-emerald-400">{stats.approved}</div>
          <div className="text-emerald-400/70 text-sm">Approved Partners</div>
        </div>
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
          <div className="text-3xl font-bold text-red-400">{stats.rejected}</div>
          <div className="text-red-400/70 text-sm">Rejected</div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or location..."
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-emerald-500/50 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2.5 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors"
          >
            Search
          </button>
        </form>

        <div className="flex items-center gap-2">
          <Filter size={18} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Colleges Table */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin text-emerald-400" size={32} />
          </div>
        ) : colleges.length === 0 ? (
          <div className="text-center py-12">
            <GraduationCap className="mx-auto text-gray-600 mb-3" size={48} />
            <p className="text-gray-400">No colleges found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">College</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Contact</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Location</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Landing</th>
                  <th className="text-right px-4 py-3 text-gray-400 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {colleges.map((college) => (
                  <tr key={college.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center overflow-hidden relative group">
                          {college.logo_url ? (
                            <img src={college.logo_url} alt={college.college_name} className="w-full h-full object-cover" />
                          ) : (
                            <GraduationCap className="text-emerald-400" size={24} />
                          )}
                          <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                            <Upload className="text-white" size={16} />
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleLogoUpload(college.id, e.target.files[0])}
                            />
                          </label>
                        </div>
                        <div>
                          <div className="font-medium text-white">{college.college_name}</div>
                          <div className="text-gray-500 text-sm">{college.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-gray-300 text-sm">{college.contact_person}</div>
                      <div className="text-gray-500 text-xs">{college.phone || 'No phone'}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-gray-300 text-sm">{college.location}</div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(college.approval_status || 'pending')}`}>
                        {(college.approval_status || 'pending').charAt(0).toUpperCase() + (college.approval_status || 'pending').slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleToggleLanding(college.id)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          college.show_on_landing 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-white/5 text-gray-500'
                        }`}
                        title={college.show_on_landing ? 'Visible on landing' : 'Hidden from landing'}
                      >
                        {college.show_on_landing ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {college.approval_status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(college.id)}
                              className="p-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors"
                              title="Approve"
                            >
                              <CheckCircle size={16} />
                            </button>
                            <button
                              onClick={() => handleReject(college.id)}
                              className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                              title="Reject"
                            >
                              <XCircle size={16} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => openEditModal(college)}
                          className="p-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(college.id)}
                          className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
            <div className="text-gray-400 text-sm">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 bg-white/5 rounded-lg text-gray-400 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 bg-white/5 rounded-lg text-gray-400 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedCollege && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a1a] border border-white/10 rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Edit College</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">College Name</label>
                <input
                  type="text"
                  value={editForm.college_name}
                  onChange={(e) => setEditForm({ ...editForm, college_name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Contact Person</label>
                <input
                  type="text"
                  value={editForm.contact_person}
                  onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Phone</label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Website</label>
                  <input
                    type="text"
                    value={editForm.website}
                    onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Location</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">City</label>
                  <input
                    type="text"
                    value={editForm.city}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">State</label>
                  <input
                    type="text"
                    value={editForm.state}
                    onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2.5 bg-white/5 text-gray-300 rounded-lg hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegesAdmin;
